/**
 * wall-art-mobile-zoom.js
 *
 * Mobile-only (≤749px) tap-to-zoom for the product gallery's hero (slot 1) and
 * the "featured" slot (the image shown 2nd). Tapping either image opens a
 * fullscreen overlay with:
 *   - pinch-to-zoom (two fingers)
 *   - double-tap to zoom in/out (toward the tapped point)
 *   - one-finger drag to pan when zoomed
 *   - swipe down (when not zoomed) / tap the dark backdrop / ✕ button to close
 *
 * Desktop is untouched: the overlay never opens and the magnifier cue is hidden
 * (see the @media rules in product-media-gallery.liquid).
 *
 * Trigger images carry `data-wall-art-zoomable` on their slide <li> (set in
 * product-media-gallery.liquid). The open handler is delegated + capture-phase
 * so it covers the format-swapped featured <img> and beats Dawn's lightbox on
 * these two slots only.
 */
(function () {
  'use strict';

  var MQ = '(max-width: 749px)';
  function isMobile() { return !!(window.matchMedia && window.matchMedia(MQ).matches); }

  var MAX_SCALE = 4;
  var ZOOM_STEP = 2.5;

  var overlay = null, stage = null, imgEl = null;
  var scale = 1, tx = 0, ty = 0;

  var pinching = false, panning = false, swiping = false, moved = false;
  var startDist = 0, startScale = 1;
  var panX0 = 0, panY0 = 0, tx0 = 0, ty0 = 0;
  var swipeY0 = 0;
  var lastTapTime = 0, lastTapX = 0, lastTapY = 0;

  function build() {
    overlay = document.createElement('div');
    overlay.className = 'wa-zoom-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');

    stage = document.createElement('div');
    stage.className = 'wa-zoom-stage';

    imgEl = document.createElement('img');
    imgEl.className = 'wa-zoom-img';
    imgEl.decoding = 'async';
    stage.appendChild(imgEl);

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'wa-zoom-close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '×';

    overlay.appendChild(stage);
    overlay.appendChild(closeBtn);
    document.body.appendChild(overlay);

    closeBtn.addEventListener('click', function (e) { e.stopPropagation(); close(); });
    stage.addEventListener('click', function (e) {
      // Tap on the dark area (not the image) closes when not zoomed.
      if (e.target !== imgEl && scale <= 1.01) close();
    });
    overlay.addEventListener('touchstart', onTouchStart, { passive: false });
    overlay.addEventListener('touchmove', onTouchMove, { passive: false });
    overlay.addEventListener('touchend', onTouchEnd, { passive: false });
    window.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) close();
    });
  }

  function apply() {
    imgEl.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + scale + ')';
  }
  function reset() { scale = 1; tx = 0; ty = 0; imgEl.style.transform = ''; }

  function clampPan() {
    var w = imgEl.clientWidth * scale;
    var h = imgEl.clientHeight * scale;
    var maxX = Math.max(0, (w - window.innerWidth) / 2);
    var maxY = Math.max(0, (h - window.innerHeight) / 2);
    if (tx > maxX) tx = maxX;
    if (tx < -maxX) tx = -maxX;
    if (ty > maxY) ty = maxY;
    if (ty < -maxY) ty = -maxY;
  }

  function open(src, alt) {
    if (!overlay) build();
    imgEl.src = src || '';
    imgEl.alt = alt || '';
    reset();
    overlay.style.opacity = '';
    overlay.classList.add('is-open');
    document.documentElement.classList.add('wa-zoom-open');
  }
  function close() {
    if (!overlay) return;
    overlay.classList.remove('is-open');
    document.documentElement.classList.remove('wa-zoom-open');
    overlay.style.opacity = '';
    reset();
  }

  function twoFingerDist(touches) {
    var dx = touches[1].clientX - touches[0].clientX;
    var dy = touches[1].clientY - touches[0].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function toggleZoomAt(px, py) {
    if (scale > 1.01) {
      reset();
    } else {
      scale = ZOOM_STEP;
      tx = -(px - window.innerWidth / 2) * (scale - 1);
      ty = -(py - window.innerHeight / 2) * (scale - 1);
      clampPan();
      apply();
    }
  }

  function onTouchStart(e) {
    moved = false;
    if (e.touches.length === 2) {
      pinching = true; panning = false; swiping = false;
      startDist = twoFingerDist(e.touches);
      startScale = scale;
      e.preventDefault();
    } else if (e.touches.length === 1) {
      var t = e.touches[0];
      if (scale > 1.01) {
        panning = true;
        panX0 = t.clientX; panY0 = t.clientY; tx0 = tx; ty0 = ty;
      } else {
        swiping = true; swipeY0 = t.clientY;
      }
    }
  }

  function onTouchMove(e) {
    if (pinching && e.touches.length === 2) {
      e.preventDefault();
      moved = true;
      if (startDist > 0) {
        scale = startScale * (twoFingerDist(e.touches) / startDist);
        if (scale > MAX_SCALE) scale = MAX_SCALE;
        if (scale < 1) scale = 1;
        clampPan();
        apply();
      }
      return;
    }
    if (e.touches.length === 1) {
      var t = e.touches[0];
      if (panning) {
        e.preventDefault();
        moved = true;
        tx = tx0 + (t.clientX - panX0);
        ty = ty0 + (t.clientY - panY0);
        clampPan();
        apply();
      } else if (swiping) {
        var dy = t.clientY - swipeY0;
        if (dy > 4) {
          e.preventDefault();
          moved = true;
          overlay.style.opacity = String(Math.max(0.25, 1 - dy / 450));
          imgEl.style.transform = 'translateY(' + dy + 'px)';
        }
      }
    }
  }

  function onTouchEnd(e) {
    if (pinching && e.touches.length < 2) {
      pinching = false;
      if (scale < 1.05) reset();
    }
    if (swiping && e.touches.length === 0) {
      swiping = false;
      var ey = (e.changedTouches[0] || {}).clientY || swipeY0;
      overlay.style.opacity = '';
      if (ey - swipeY0 > 90) { close(); return; }
      apply(); // snap back
    }
    if (panning && e.touches.length === 0) panning = false;

    // Double-tap (a clean single-finger tap with no drag/pinch) toggles zoom.
    if (e.touches.length === 0 && e.changedTouches.length === 1 && !moved) {
      var t = e.changedTouches[0];
      var now = Date.now();
      if (now - lastTapTime < 300 &&
          Math.abs(t.clientX - lastTapX) < 32 &&
          Math.abs(t.clientY - lastTapY) < 32) {
        toggleZoomAt(t.clientX, t.clientY);
        lastTapTime = 0;
      } else {
        lastTapTime = now; lastTapX = t.clientX; lastTapY = t.clientY;
      }
    }
  }

  // Open on tap of a zoomable slide image (mobile only). Capture phase +
  // stopPropagation so Dawn's lightbox / modal-opener doesn't also fire.
  document.addEventListener('click', function (e) {
    if (!isMobile()) return;
    var target = e.target;
    if (!target || !target.closest) return;
    var img = target.closest('[data-wall-art-zoomable] img');
    if (!img) return;
    e.preventDefault();
    e.stopPropagation();
    open(img.currentSrc || img.src, img.alt);
  }, true);
})();
